import { BarChart } from '@ssc-ui/charts-react';
import { BarChartProps } from '@ssc-ui/charts-react/es/components/bar-chart/types';
import { useRequest } from 'ahooks';
import * as React from 'react';
import { Spin } from 'ssc-ui-react';

import { $gt } from '@/common/appCtx';

import { getTopOperators } from '../../api';
import useRefreshInterval from '../../hooks/use-refresh-interval';
import Empty from '../empty';
import styles from './index.module.less';

export interface TopOperatorsProps {
  workstationGroupId?: number;
  workstationGroupName?: string;
}

const TopOperators: React.FC<TopOperatorsProps> = (props) => {
  // use any because of no type export
  const chartInstance = React.useRef<any>({});
  const { data, loading, run: manualGetTopOperators } = useRequest(getTopOperators, {
    manual: true
  });

  const topOperators = React.useMemo(() => {
    return data?.data?.items || [];
  }, [data?.data?.items]);

  const dataSource = React.useMemo(() => {
    return topOperators
      .map((item) => ({
        type: item.operator,
        value: item.throughput,
      })).reduce((acc, curr) => {
        acc.unshift(curr);
        return acc;
      }, [] as any[]);
  }, [topOperators]);

  const refresh = React.useCallback(() => {
    if (props.workstationGroupId) {
      manualGetTopOperators({
        workstation_group_id: props.workstationGroupId
      });
    }
  }, [manualGetTopOperators, props.workstationGroupId]);

  useRefreshInterval(refresh, true);

  const barChartProps = React.useMemo(() => {
    return {
      dataSource,
      onChartReady: instance => {
        chartInstance.current = instance;
      },
      echartsOption: {
        grid: {
          top: 0,
          bottom: 0,
        },
        yAxis: {
          axisLabel: { show: false },
          axisLine: { lineStyle: { color: '#fff', opacity: 0.4 } },
        },
        xAxis: {
          axisLabel: {
            color: '#fff',
            fontWeight: 500,
          },
          splitLine: {
            lineStyle: {
              color: '#fff',
              opacity: 0.4
            }
          },
        }
      },
      config:{
        xField: 'type',
        yField: ['value'],
        legend: { show: false },
        barWidth: 16,
        dataLabel: {
          show: true,
        },
      }
    } as BarChartProps;
  }, [dataSource]);

  const renderContent = () => {
    if (!topOperators.length) {
      return (
        <div className={styles.empty}>
          <Empty />
        </div>
      );
    }
    return (
      <>
        <div className={styles.yAxis}>
          {topOperators.map((item, index) => (
            <div
              key={index}
              className={styles.yAxisItem}
            >
              <div
                className={styles.operator}
                title={`${index + 1} ${item.operator}`}
              >
                {index + 1} {item.operator}
              </div>
              <span className={styles.workstation}>{item.workstation}</span>
            </div>
          ))}
        </div>
        <BarChart
          className={styles.chart}
          {...barChartProps}
        />
      </>
    );
  };

  const resize = React.useCallback(() => {
    chartInstance.current?.resize();
  }, []);

  React.useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [dataSource, resize]);

  return (
    <div className={styles.topOperators}>
      <div
        className={styles.head}
        title={props.workstationGroupName}
      >
        <div>{$gt('Top Operators Order Completion - ')}</div>
        <div>{props.workstationGroupName}</div>
      </div>
      <Spin
        className={styles.chartWrapper}
        spinning={loading}
        size="large"
      >
        {renderContent()}
      </Spin>
    </div>
  );
};

export default TopOperators;
